import { createFileRoute, Link } from "@tanstack/react-router";
import { Download } from "lucide-react";
import { CarryFactorDialog } from "@/lovable-demo/components/carry-factor-dialog";
import { EscalatedFinancialExposure } from "@/lovable-demo/components/escalated-financial-exposure";
import { AppShell } from "@/lovable-demo/components/app-shell";
import { KpiCard, Panel, VerdictBadge } from "@/lovable-demo/components/bits";
import { CostForecastChart, WaterfallChart } from "@/lovable-demo/components/charts";
import { Button } from "@/components/ui/button";
import {
  blendedForecast,
  portfolio,
  recordsForProgram,
} from "@/lovable-demo/lib/warranty/aggregate";
import {
  fmtCompactCurrency,
  fmtCurrency,
  recordDelayCost,
  recordNpr,
  recordVerdict,
} from "@/lovable-demo/lib/warranty/calc";
import { downloadCsv } from "@/lovable-demo/lib/warranty/export";
import { useWarranty } from "@/lovable-demo/lib/warranty/store";

export const Route = createFileRoute("/demo/financial")({
  head: () => ({
    meta: [
      { title: "Financial Intelligence — Broadview Warranty" },
      {
        name: "description",
        content:
          "Warranty exposure waterfall, delay cost of inaction, projected cost and supplier recovery tracking by Problem Record.",
      },
      { property: "og:title", content: "Financial Intelligence — Broadview Warranty" },
      {
        property: "og:description",
        content:
          "Warranty exposure waterfall, delay cost of inaction, projected cost and supplier recovery tracking by Problem Record.",
      },
    ],
  }),
  component: Financial,
});

function Financial() {
  const { data, activeProgramId, defaultCarryFactor, changeDefaultCarryFactor } = useWarranty();
  const records = recordsForProgram(data, activeProgramId);
  const stats = portfolio(records);
  const forecast = blendedForecast(records);
  const program = data.programs.find((p) => p.id === activeProgramId);

  const waterfall = [
    { label: "Gross exposure", value: stats.exposure, kind: "total" as const },
    { label: "Incurred to date", value: -stats.actual, kind: "decrease" as const },
    { label: "Supplier recovery", value: -stats.recoveryTarget, kind: "decrease" as const },
    { label: "Delay carry (1 quarter)", value: stats.delay * 3, kind: "increase" as const },
    {
      label: "Read-across risk",
      value:
        records.reduce(
          (a, r) =>
            a +
            r.readAcross.filter((x) => x.status !== "cleared").reduce((s, x) => s + x.exposure, 0),
          0,
        ) * 0.25,
      kind: "increase" as const,
    },
  ];

  const csv = () =>
    downloadCsv(
      "broadview-financial-exposure.csv",
      records.map((r) => ({
        Code: r.code,
        Title: r.title,
        "Actual cost": r.financial.actualCost,
        Exposure: r.financial.exposure,
        "Remaining exposure": recordNpr(r),
        "Carry factor": r.financial.monthlyCarryFactor,
        "Delay cost / month": Math.round(recordDelayCost(r)),
        "Recovery target": r.financial.supplierRecoveryTarget,
        Recovered: r.financial.supplierRecovered,
        "Target reduction %": r.financial.targetReductionPct,
        "Observed reduction %": r.financial.observedReductionPct,
        Effectiveness: recordVerdict(r.financial),
      })),
    );

  return (
    <AppShell
      title="Financial Intelligence"
      subtitle={
        program
          ? `${program.code} · ${program.name} · MY${program.modelYear}`
          : "All programs · consolidated portfolio"
      }
      actions={
        <Button variant="outline" size="sm" onClick={csv}>
          <Download className="size-3.5" /> CSV
        </Button>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="Gross exposure"
          value={fmtCompactCurrency(stats.exposure)}
          tone="bad"
          hint="Actual plus projected"
        />
        <KpiCard
          label="Incurred to date"
          value={fmtCompactCurrency(stats.actual)}
          hint="Booked warranty cost"
        />
        <KpiCard
          label="Remaining exposure"
          value={fmtCompactCurrency(stats.npr)}
          tone="good"
          hint="Total exposure minus actual cost to date"
        />
        <KpiCard
          label="Delay cost per month"
          value={fmtCompactCurrency(stats.delay)}
          tone="warn"
          hint="Remaining exposure × monthly carry factor"
        />
      </div>

      <div className="mt-4">
        <EscalatedFinancialExposure records={records} />
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-2">
        <Panel title="Exposure waterfall" subtitle="From gross exposure to residual risk">
          <WaterfallChart items={waterfall} />
        </Panel>
        <Panel
          title="Cost vs projected cost"
          subtitle="Month-over-month growth projection; not an AI or statistical model"
        >
          <CostForecastChart data={forecast} />
        </Panel>
      </div>

      <div className="mt-4">
        <Panel
          title="Default delay carry factor"
          subtitle="Applied to future records unless a governed per-record value is set"
          actions={
            <CarryFactorDialog
              current={defaultCarryFactor}
              label="Change default"
              onConfirm={changeDefaultCarryFactor}
            />
          }
          className="mb-4"
        >
          <p className="num text-lg font-semibold">
            {(defaultCarryFactor * 100).toFixed(1)}% per month
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Covers continued claims accrual, containment and inspection cost, and carrying cost
            while remaining exposure is unaddressed.
          </p>
        </Panel>
        <Panel
          title="Exposure and recovery by record"
          subtitle={`${fmtCurrency(stats.recovered)} recovered of ${fmtCurrency(stats.recoveryTarget)} claimed`}
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[940px] text-sm">
              <thead className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-2 py-2 text-left font-medium">Record</th>
                  <th className="px-2 py-2 text-right font-medium">Actual</th>
                  <th className="px-2 py-2 text-right font-medium">Exposure</th>
                  <th className="px-2 py-2 text-right font-medium">Remaining exposure</th>
                  <th className="px-2 py-2 text-right font-medium">Delay / mo</th>
                  <th className="px-2 py-2 text-right font-medium">Recovery</th>
                  <th className="px-2 py-2 text-left font-medium">Effectiveness</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {[...records]
                  .sort((a, b) => b.financial.exposure - a.financial.exposure)
                  .map((r) => (
                    <tr key={r.id} className="hover:bg-elevated/60">
                      <td className="px-2 py-2.5">
                        <Link
                          to="/demo/problem-record/$id"
                          params={{ id: r.id }}
                          className="hover:text-primary"
                        >
                          <span className="num block text-xs text-primary">{r.code}</span>
                          <span className="block max-w-[280px] truncate">{r.title}</span>
                        </Link>
                      </td>
                      <td className="num px-2 py-2.5 text-right">
                        {fmtCompactCurrency(r.financial.actualCost)}
                      </td>
                      <td className="num px-2 py-2.5 text-right">
                        {fmtCompactCurrency(r.financial.exposure)}
                      </td>
                      <td className="num px-2 py-2.5 text-right text-success">
                        {fmtCompactCurrency(recordNpr(r))}
                      </td>
                      <td className="num px-2 py-2.5 text-right text-warning">
                        {fmtCompactCurrency(recordDelayCost(r))}
                      </td>
                      <td className="num px-2 py-2.5 text-right">
                        {fmtCompactCurrency(r.financial.supplierRecovered)}
                        <span className="text-muted-foreground">
                          {" "}
                          / {fmtCompactCurrency(r.financial.supplierRecoveryTarget)}
                        </span>
                      </td>
                      <td className="px-2 py-2.5">
                        <VerdictBadge verdict={recordVerdict(r.financial)} />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </AppShell>
  );
}
